// What the Studio's own pages tell the event log (src/core/events.ts lists
// what a page may say; the server refuses the rest). The page sends a name
// and facts from fixed lists. It sends no identifier: the server attaches the
// user id of the session the request already carries.
//
// Where the visit came from (the code its launch link carried and the class
// of the page before, ./visit.ts) rides only on events the server records
// for nobody. An event of a person that marks a step of the visit, such as
// the sign-in card shown, is said as it is, and the step is said beside it as
// `visit.step`, which names nobody and carries the code and the class.
//
// Nothing is sent from a browser that asks not to be tracked (Global Privacy
// Control or Do Not Track), and the server discards what such a browser sends
// anyway. Nothing is sent until `enableEvents`, which the room calls once it
// knows it has a server behind it.

import { STEP_OF, VISIT_EVENTS } from "../core/campaign.ts";
import type { EventName, PageEvent } from "../core/events.ts";
import type { StudioCore } from "../core/studio.ts";
import { currentVisit } from "./visit.ts";

const ENDPOINT = "/api/events";
const queue: PageEvent[] = [];
let on = false;
let timer: ReturnType<typeof setTimeout> | null = null;

const optedOut = (): boolean =>
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true || navigator.doNotTrack === "1";

function flush(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!on || queue.length === 0) return;
  const body = JSON.stringify({ events: queue.splice(0, 20) });
  try {
    // A beacon outlives the page; where there is none, a kept-alive request does the same.
    if (!navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: "application/json" }))) {
      void fetch(ENDPOINT, { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true, credentials: "same-origin" }).catch(() => null);
    }
  } catch {
    // An event is never a reason for the page to fail.
  }
  if (queue.length > 0) timer = setTimeout(flush, 200);
}

/** Say that something happened. Events said before `enableEvents` wait for it. */
export function track(name: EventName, fields: Omit<PageEvent, "name"> = {}): void {
  if (optedOut()) return;
  put({ name, ...fields });
  // The step of the visit this event marks, said once beside it and for nobody: the event itself says nothing of where the visit came from.
  const step = STEP_OF[name];
  if (step) put({ name: "visit.step", ...(fields.app ? { app: fields.app } : {}), props: { step } });
}

/** Queues one event as it will be sent. */
function put(event: PageEvent): void {
  // Where the visit came from rides on each event the catalogue lets carry it, all of them recorded with no user id: the code of a launch link, and the class of the page before.
  if (VISIT_EVENTS.includes(event.name)) {
    const { c, ref } = currentVisit();
    event.props = { ...(c ? { c } : {}), ref, ...event.props };
  }
  queue.push(event);
  if (queue.length > 60) queue.splice(0, queue.length - 60);
  if (!on) return;
  if (queue.length >= 10) flush();
  else if (!timer) timer = setTimeout(flush, 1200);
}

/** Start sending: the page has a server. */
export function enableEvents(): void {
  if (on) return;
  on = true;
  // The visit is read with the page, so a code in the address is the tab's before anything is said.
  currentVisit();
  addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) flush();
  });
  if (queue.length > 0) timer = setTimeout(flush, 300);
}

// A part of the page that does not import this module says an event with
// `dispatchEvent(new CustomEvent("pocket:event", { detail: { name, app, device, props } }))`.
addEventListener("pocket:event", (event) => {
  const detail = (event as CustomEvent<PageEvent | undefined>).detail;
  if (detail && typeof detail.name === "string") track(detail.name, { app: detail.app, device: detail.device, props: detail.props });
});

/** The room's steps as events: what the visitor turned to, read from the core's focus. */
export function watch(core: StudioCore): void {
  core.subscribe((state, previous) => {
    const focus = state.focus;
    const before = previous.focus;
    if (focus === before || JSON.stringify(focus) === JSON.stringify(before)) return;
    switch (focus.kind) {
      case "device": return track("device.picked", { device: focus.device });
      case "app": return track("card.opened", { app: focus.app });
      case "play": return track("play.started", { app: focus.app, device: focus.device });
      case "tv": return track("tv.opened");
      case "laptop": return track("laptop.opened");
      case "closet": return track("closet.opened");
      default: return;
    }
  });
}

/**
 * What brought the visitor here, said once: a game's own address (the link
 * carries `from=player`, or the page before was one label below this host),
 * a game's page (`from=listing`) or a share link. `from` is taken out of the
 * address afterwards.
 */
export function arrived(app: string | null): void {
  const url = new URL(location.href);
  const fromListing = url.searchParams.get("from") === "listing";
  let fromPlayer = url.searchParams.get("from") === "player";
  try {
    if (!fromPlayer && document.referrer) fromPlayer = new URL(document.referrer).hostname.endsWith(`.${location.hostname}`);
  } catch {
    // A referrer that is not an address says nothing.
  }
  if (url.searchParams.has("from")) {
    url.searchParams.delete("from");
    history.replaceState(null, "", url);
  }
  if (fromListing) track("studio.arrived", { app: app ?? undefined, props: { from: "listing" } });
  else if (fromPlayer) track("studio.arrived", { app: app ?? undefined, props: { from: "player" } });
  else if (app) track("studio.arrived", { app, props: { from: "share" } });
}
